import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiMapPin, FiSend, FiCheck, FiMessageCircle, FiClock } from 'react-icons/fi';
import { toast } from 'sonner';
import axios from 'axios';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';

const fieldClass =
  'min-h-[44px] w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/api/v1/contact', formData);
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try emailing us directly.');
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: FiMail,
      title: 'Email Us',
      description: 'We typically respond within 24 hours',
      value: 'support@tasklina.com',
      href: 'mailto:support@tasklina.com',
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300'
    },
    {
      icon: FiMapPin,
      title: 'Location',
      description: 'Remote-first company',
      value: 'Global Team',
      href: null,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300'
    },
    {
      icon: FiClock,
      title: 'Business Hours',
      description: 'Mon - Fri',
      value: '9:00 AM - 6:00 PM IST',
      href: null,
      color: 'bg-violet-100 text-violet-600 dark:bg-violet-500/20 dark:text-violet-300'
    }
  ];

  const faqs = [
    {
      question: 'How do I get started with Tasklina?',
      answer: 'Simply sign up for a free account and you can start creating projects and tasks immediately. No credit card required.'
    },
    {
      question: 'Is there a free plan available?',
      answer: 'Yes! We offer a generous free plan that includes essential features for individuals and small teams.'
    },
    {
      question: 'Can I upgrade or downgrade my plan anytime?',
      answer: 'Absolutely. You can change your plan at any time from your account settings. Changes take effect immediately.'
    },
    {
      question: 'Do you offer enterprise solutions?',
      answer: 'Yes, we offer custom enterprise plans with advanced features, dedicated support, and custom integrations. Contact us for details.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />

      {/* Hero */}
      <section className="border-b border-gray-100 bg-gradient-to-b from-blue-50 to-white py-10 dark:border-gray-800 dark:from-gray-800/80 dark:to-gray-900 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center sm:text-left">
            <div className="mb-4 inline-flex rounded-2xl bg-blue-100 p-3 dark:bg-blue-500/20 sm:mb-5">
              <FiMessageCircle className="h-7 w-7 text-blue-600 dark:text-blue-300 sm:h-8 sm:w-8" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
              Get in Touch
            </h1>
            <p className="mt-3 text-base leading-relaxed text-gray-600 dark:text-gray-300 sm:mt-4 sm:text-lg">
              Have a question or feedback? We'd love to hear from you. Our team is here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact methods */}
      <section className="border-b border-gray-100 py-8 dark:border-gray-800 sm:py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 md:gap-5">
            {contactMethods.map((method) => {
              const Icon = method.icon;
              return (
                <div
                  key={method.title}
                  className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 sm:p-5"
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${method.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">{method.title}</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">{method.description}</p>
                    {method.href ? (
                      <a
                        href={method.href}
                        className="mt-2 inline-block text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {method.value}
                      </a>
                    ) : (
                      <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{method.value}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form + FAQ */}
      <section className="py-10 sm:py-14">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-10 xl:gap-12">
            {/* Contact form */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 sm:p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Send Us a Message</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Fill out the form and we will reply within one business day.
              </p>

              {submitted ? (
                <div className="mt-6 rounded-2xl bg-green-50 p-6 text-center dark:bg-green-900/20 sm:p-8">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-800/50">
                    <FiCheck className="h-7 w-7 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Message Sent!</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-5 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={fieldClass}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={fieldClass}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className={`${fieldClass} appearance-none`}
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="enterprise">Enterprise Sales</option>
                      <option value="partnership">Partnership Opportunity</option>
                      <option value="feedback">Product Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className={`${fieldClass} min-h-[120px] resize-none py-3`}
                      placeholder="How can we help you?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <FiSend className="h-5 w-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* FAQs */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Frequently Asked Questions</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Quick answers before you reach out.
              </p>

              <div className="mt-5 space-y-3">
                {faqs.map((faq) => (
                  <div
                    key={faq.question}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60 sm:p-5"
                  >
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                      {faq.question}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 p-5 text-white sm:mt-6 sm:p-6">
                <h3 className="text-base font-semibold sm:text-lg">Need immediate help?</h3>
                <p className="mt-1 text-sm leading-relaxed text-blue-100">
                  Check out our Help Center for instant answers to common questions.
                </p>
                <Link
                  to="/help"
                  className="mt-4 inline-flex min-h-[40px] items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50 dark:bg-gray-950 dark:text-white dark:hover:bg-gray-900"
                >
                  Visit Help Center
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
};

export default Contact;
